import { StoreWithProps, useClassState } from "@web-monorepo/hooks";
import { useEffect } from "react";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import logEvent from "src/utils/logEvent";
import type { Tool, ToolPayload } from "./EditableSetup";

export type UseEditableToolProps = {
  classId: string;
  toolType: string;
  /**
   * Creates a new tool. Receives a flat payload (and optional dojoId for
   * promoting a suggested tool to a user-owned one). Resolved against the
   * parent class via the container.
   */
  createToolApi: (data: { classId: string; type: string; payload: ToolPayload; dojoId?: string }) => void;
  editToolApi: (data: { classId: string; _id: string; payload: ToolPayload; updatedAt: string }) => void;
  deleteToolApi: (data: { _id: string; classId: string }) => void;
  displayOpen: (path: string, displayPayload?: ToolkitAction) => void;
  getDisplayPayload: (tool: Tool) => ToolkitAction;
};

export type UseEditableToolResult = {
  editScreen: boolean;
  selectedTool: Tool | null;
  openEditScreen: (tool?: Tool | null) => void;
  closeEditScreen: () => void;
  showTool: (tool: Tool) => void;
  clearSelectedTool: () => void;
  createTool: (payload: ToolPayload, dojoId?: string) => void;
  editTool: (toolId: string, payload: ToolPayload) => void;
  deleteTool: (toolId?: string) => void;
};

/**
 * Valtio-backed store for EditableTool's three-screen state machine:
 *   - saved-tools grid (default)
 *   - edit screen (`editScreen=true`)
 *   - result/cast view (`selectedTool` set, `editScreen=false`)
 *
 * Wraps the create/edit/delete API callbacks with the parent's classId +
 * toolType so render-side code doesn't repeat those.
 */
class EditableToolStore extends StoreWithProps<UseEditableToolProps> {
  editScreen = false;
  selectedTool: Tool | null = null;

  start() {
    logEvent({ eventName: `${this.props.toolType}.setup.open` });
  }

  stop() {
    logEvent({ eventName: `${this.props.toolType}.setup.close` });
  }

  openEditScreen(tool: Tool | null = null) {
    this.editScreen = true;
    this.selectedTool = tool;
  }

  closeEditScreen() {
    this.editScreen = false;
    this.selectedTool = null;
  }

  showTool(tool: Tool) {
    this.editScreen = false;
    this.selectedTool = tool;

    this.props.displayOpen(`/apps/display/${this.props.toolType}`, this.props.getDisplayPayload(tool));
    this.updateLastUsed(tool);
  }

  clearSelectedTool() {
    this.selectedTool = null;
  }

  createTool(payload: ToolPayload, dojoId?: string) {
    this.props.createToolApi({
      classId: this.props.classId,
      type: this.props.toolType,
      payload,
      dojoId,
    });
  }

  editTool(toolId: string, payload: ToolPayload) {
    this.props.editToolApi({
      classId: this.props.classId,
      _id: toolId,
      payload,
      updatedAt: new Date().toISOString(),
    });
  }

  deleteTool(toolId?: string) {
    if (!toolId) return;
    this.props.deleteToolApi({
      _id: toolId,
      classId: this.props.classId,
    });
    this.editScreen = false;
    this.selectedTool = null;
  }

  /**
   * Best-effort last-used tracking — fires alongside the cast `displayOpen`
   * so the saved-tools grid can sort by recency. The server round-trip is
   * intentionally not awaited; if it fails silently, the display still opens
   * but the timestamp doesn't bump (acceptable tradeoff for cast latency).
   *
   * For a Tool with `_id` (saved): edit the existing record to bump
   * `updatedAt`. For a Tool with only `dojoId` (suggested template the user
   * hasn't saved): promote it to a real saved tool the first time the
   * teacher casts it.
   */
  private updateLastUsed(tool: Tool) {
    const { _id, dojoId, payload } = tool;
    if (_id) {
      this.editTool(_id, payload);
    } else if (dojoId) {
      this.createTool(payload, dojoId);
    }
  }
}

export function useEditableTool(props: UseEditableToolProps): UseEditableToolResult {
  const store = useClassState(EditableToolStore, props);
  const { start, stop } = store;

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return {
    editScreen: store.editScreen,
    selectedTool: store.selectedTool,
    openEditScreen: store.openEditScreen,
    closeEditScreen: store.closeEditScreen,
    showTool: store.showTool,
    clearSelectedTool: store.clearSelectedTool,
    createTool: store.createTool,
    editTool: store.editTool,
    deleteTool: store.deleteTool,
  };
}
